import React, { useEffect, useState } from "react";
import api, { formatApiError, uploadUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, EmptyState, Field } from "../components/ui-ext";
import StatusPill from "../components/StatusPill";
import { Check, X, Wallet, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ExpenseApprovals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("submitted");
  const [selected, setSelected] = useState(null);
  const [payingId, setPayingId] = useState(null);

  const load = () => api.get("/expenses", { params: { status: filter || undefined } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const isAdmin = user.role === "admin";

  const doReview = async (id, level, action, approved_amount, remarks) => {
    try {
      await api.post(`/expenses/${id}/${level}-review`, { action, approved_amount, remarks });
      toast.success(`Expense ${action}d`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Approvals"
        title="Expense Approvals"
        subtitle={isAdmin ? "Review manager-approved expenses, give final approval & record payments." : "Approve or modify engineer expense claims."}
      />
      <div className="flex gap-3 mb-6 flex-wrap">
        {[
          { v: "submitted", l: "Awaiting Manager" },
          ...(isAdmin ? [{ v: "manager_approved", l: "Awaiting HOD" }] : []),
          { v: "admin_approved", l: "Ready for Payment" },
          { v: "paid", l: "Paid" },
          { v: "rejected", l: "Rejected" },
        ].map((tab) => (
          <button
            key={tab.v}
            onClick={() => setFilter(tab.v)}
            data-testid={`approvals-tab-${tab.v}`}
            className={`mono text-[10px] uppercase tracking-widest px-3 py-2 border rounded-sm transition-colors ${filter === tab.v ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-slate-400 hover:border-white/30"}`}
          >
            {tab.l}
          </button>
        ))}
      </div>

      <Card>
        {items.length === 0 ? (
          <EmptyState title="Nothing to review" hint="All caught up." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full tech-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Engineer</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Approved</th>
                  <th>Description</th>
                  <th>Bills</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} data-testid={`approval-row-${e.id}`}>
                    <td className="mono text-xs">{e.date}</td>
                    <td>{e.engineer_name}</td>
                    <td className="capitalize">{e.category?.replace("_", " ")}</td>
                    <td className="mono">₹ {e.amount}</td>
                    <td className="mono text-green-400">{e.approved_amount != null ? `₹ ${e.approved_amount}` : "—"}</td>
                    <td className="text-xs text-slate-400 max-w-[220px] truncate">{e.description || "—"}</td>
                    <td>
                      {(e.bill_photos || []).length > 0 ? (
                        <div className="flex gap-1">
                          {e.bill_photos.slice(0, 2).map((url, i) => (
                            <a key={i} href={uploadUrl(url)} target="_blank" rel="noreferrer" className="w-8 h-8 border border-white/10 overflow-hidden block">
                              <img src={uploadUrl(url)} alt="" className="w-full h-full object-cover" />
                            </a>
                          ))}
                          {e.bill_photos.length > 2 && <span className="mono text-xs text-slate-500 self-center">+{e.bill_photos.length - 2}</span>}
                        </div>
                      ) : <span className="text-slate-600">—</span>}
                    </td>
                    <td><StatusPill status={e.status} /></td>
                    <td>
                      {e.status === "submitted" && (
                        <div className="flex gap-1">
                          <button onClick={() => setSelected({ id: e.id, level: "manager", exp: e })} className="border border-white/15 hover:border-[#FF5722] text-xs px-2 py-1 mono uppercase" data-testid={`review-${e.id}`}>Review</button>
                        </div>
                      )}
                      {e.status === "manager_approved" && isAdmin && (
                        <div className="flex gap-1">
                          <button onClick={() => setSelected({ id: e.id, level: "admin", exp: e })} className="border border-white/15 hover:border-[#FF5722] text-xs px-2 py-1 mono uppercase" data-testid={`final-${e.id}`}>Final</button>
                        </div>
                      )}
                      {e.status === "admin_approved" && isAdmin && (
                        <button onClick={() => setPayingId(e.id)} className="bg-[#FF5722] text-black text-xs px-2 py-1 mono uppercase flex items-center gap-1" data-testid={`pay-${e.id}`}>
                          <Wallet className="w-3 h-3" /> Pay
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected && (
        <ReviewDialog
          data={selected}
          onClose={() => setSelected(null)}
          onSubmit={(action, amt, remarks) => doReview(selected.id, selected.level, action, amt, remarks)}
        />
      )}
      {payingId && (
        <PayDialog
          expenseId={payingId}
          expense={items.find((x) => x.id === payingId)}
          onClose={() => setPayingId(null)}
          onSaved={() => { setPayingId(null); load(); }}
        />
      )}
    </div>
  );
}

function ReviewDialog({ data, onClose, onSubmit }) {
  const [amt, setAmt] = useState(data.exp.approved_amount ?? data.exp.amount);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (action) => {
    setBusy(true);
    try {
      await onSubmit(action, action === "reject" ? null : amt, remarks);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-white/10 sticky top-0 bg-[#13151A]">
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF5722]">{data.level === "admin" ? "Final HOD Approval" : "Manager Review"}</div>
          <h2 className="text-lg font-semibold">Review Expense</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><div className="mono text-[10px] text-slate-500 uppercase">Engineer</div><div>{data.exp.engineer_name}</div></div>
            <div><div className="mono text-[10px] text-slate-500 uppercase">Category</div><div className="capitalize">{data.exp.category}</div></div>
            <div><div className="mono text-[10px] text-slate-500 uppercase">Claimed</div><div className="mono">₹ {data.exp.amount}</div></div>
            <div><div className="mono text-[10px] text-slate-500 uppercase">Date</div><div>{data.exp.date}</div></div>
          </div>

          {(data.exp.journey_legs || []).length > 0 && (
            <div className="border border-white/10 bg-[#0B0C10] p-3">
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF5722] mb-2">Journey Breakdown</div>
              <div className="space-y-1.5">
                {data.exp.journey_legs.map((l, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs border-b border-white/5 pb-1.5 last:border-0">
                    <span className="mono text-[10px] text-[#FF5722] w-6">#{i + 1}</span>
                    <span className="flex-1 text-slate-200">
                      {l.from_point} <span className="text-slate-500">→</span> {l.to_point}
                      <span className="mono text-slate-500 ml-2">
                        ({l.mode}{l.distance_km ? `, ${l.distance_km}km` : ""})
                      </span>
                      {l.remarks && <div className="text-slate-500 italic mt-0.5">{l.remarks}</div>}
                    </span>
                    <span className="mono text-slate-100">₹ {l.amount}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.exp.description && (
            <div>
              <div className="mono text-[10px] text-slate-500 uppercase mb-1">Description</div>
              <div className="text-sm text-slate-300">{data.exp.description}</div>
            </div>
          )}

          <Field label="Approved Amount (₹)">
            <input type="number" className="input mono" value={amt} onChange={(e) => setAmt(parseFloat(e.target.value) || 0)} data-testid="review-amount" />
          </Field>
          <Field label="Remarks"><textarea rows={2} className="input" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="review-remarks" /></Field>
        </div>
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={() => submit("reject")} disabled={busy} className="border border-red-500/40 text-red-400 hover:bg-red-500/10 px-4 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="review-reject">
            <X className="w-4 h-4" /> Reject
          </button>
          <button onClick={() => submit("approve")} disabled={busy} className="bg-[#FF5722] hover:bg-[#F4511E] text-white px-5 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="review-approve">
            <Check className="w-4 h-4" /> Approve
          </button>
        </div>
        <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
      </div>
    </div>
  );
}

function PayDialog({ expenseId, expense, onClose, onSaved }) {
  const [form, setForm] = useState({
    expense_id: expenseId,
    amount: expense?.approved_amount || 0,
    utr: "",
    from_account: "SBI - Rajat",
    paid_on: new Date().toISOString().slice(0, 10),
    remarks: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/expenses/${expenseId}/pay`, form);
      toast.success("Payment recorded");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 w-full max-w-md">
        <div className="px-6 py-4 border-b border-white/10">
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF5722]">Record Payment</div>
          <h2 className="text-lg font-semibold">Pay Expense</h2>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Amount (₹)"><input type="number" className="input mono" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} data-testid="pay-amount" /></Field>
          <Field label="UTR / Ref No."><input className="input mono" value={form.utr} onChange={(e) => setForm({ ...form, utr: e.target.value })} data-testid="pay-utr" /></Field>
          <Field label="From Account"><input className="input" value={form.from_account} onChange={(e) => setForm({ ...form, from_account: e.target.value })} /></Field>
          <Field label="Paid On"><input type="date" className="input" value={form.paid_on} onChange={(e) => setForm({ ...form, paid_on: e.target.value })} /></Field>
          <Field label="Remarks"><textarea rows={2} className="input" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="bg-[#FF5722] hover:bg-[#F4511E] px-5 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="pay-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Mark Paid
          </button>
        </div>
        <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
      </div>
    </div>
  );
}
